import * as styles from "./CameraCapture.css";

import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";

import { Button, ButtonGroup } from "@frontend/components/atoms/Button";
import { DialogClose, DialogFooter } from "@frontend/components/atoms/Dialog";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTriggerButton,
} from "@frontend/components/atoms/Menu";
import { useLingui } from "@lingui/react/macro";

import type { CameraCaptureProps } from "./CameraCapture.types";
import { ImageEditorBody } from "./ImageEditorBody";
import { useVideoDevices } from "./useVideoDevices";

function deviceLabel(device: MediaDeviceInfo, index: number): string {
  return device.label || `Camera ${index + 1}`;
}

export function CameraCapture({ onCapture }: CameraCaptureProps): JSX.Element {
  const { t } = useLingui();

  const { devices, isLoading, hasPermission, hasCameras, hasMultipleCameras } =
    useVideoDevices();

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [failed, setFailed] = useState(false);
  const [source, setSource] = useState<MediaDeviceInfo | null>(null);

  useEffect(() => {
    let active = true;

    navigator.mediaDevices
      .getUserMedia({
        video: source ? { deviceId: { exact: source.deviceId } } : true,
      })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });

    return () => {
      active = false;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [source]);

  const handleCapture = () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);

    onCapture(canvas.toDataURL("image/jpeg", 0.95));
  };

  const denied = !isLoading && !hasPermission;

  return (
    <>
      <ImageEditorBody>
        {failed || denied ? (
          <p className={styles.message}>
            {denied
              ? t`Camera access not allowed`
              : t`Unable to access the camera`}
          </p>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={styles.video}
          />
        )}
      </ImageEditorBody>

      <DialogFooter>
        <DialogClose
          render={<Button variant="secondary" label={t`Cancel`} />}
        />

        <ButtonGroup>
          <Button
            variant="primary"
            loading={isLoading}
            disabled={isLoading || !hasCameras}
            leftSection={<Icon name="Circle" />}
            label={t`Capture`}
            onClick={handleCapture}
          />
          {hasMultipleCameras && (
            <Menu align="end" side="bottom">
              <MenuTriggerButton variant="primary" />
              <MenuContent>
                {devices.map((device, index) => (
                  <MenuItem
                    key={device.deviceId}
                    onClick={() => {
                      setFailed(false);
                      setSource(device);
                    }}
                  >
                    <Icon name="Camera" />
                    {deviceLabel(device, index)}
                  </MenuItem>
                ))}
              </MenuContent>
            </Menu>
          )}
        </ButtonGroup>
      </DialogFooter>
    </>
  );
}
